import { useCallback, useEffect, useState } from 'react';
import { getJson, type ResponseSchema } from '../api';

/**
 * 조회 훅 하나 (19회차, 콘솔 검토 A-2 · Q-M9).
 *
 * 16회차까지 같은 모양의 fetch 훅이 일곱 벌 있었다(`useTxDetail`·`useDbEvents`·`useAlerts`·
 * `useProfiles` 둘·`useStorage`·검색 화면의 인라인 조회). 전부 AbortController를 만들고
 * loading·error를 들고 새로고침 tick을 세는 같은 코드였고, 검색 화면 하나만 취소를 안 했다.
 *
 * 규칙 셋:
 *   - 주소가 `undefined`면 부르지 않는다. 조건이 아직 안 갖춰진 화면이 빈 조회를 던지지 않는다
 *   - 취소된 뒤에는 상태를 건드리지 않는다. 화면이 바뀐 뒤 늦게 온 답이 앞의 답을 덮지 않는다
 *   - 받은 자리에서 계약으로 검증한다(17회차). 서버가 필드를 빼면 그리기 전에 걸린다
 *
 * 주소가 바뀌어도 새 답이 올 때까지 앞 답(`data`)을 들고 있다 — 목록 화면이 새로 읽는 동안 표가
 * 사라졌다 나타나지 않게. 대신 그 답이 **지금 주소 것인지**를 `stale`로 알린다 (44회차). 렌더 중에
 * 계산하므로 주소가 바뀐 첫 그림부터 맞다(효과가 돌아 `loading`이 서기 전에도). 앞 답을 그리면 안
 * 되는 화면(상세)이 이것을 본다.
 */

export interface QueryState<T> {
  loading: boolean;
  data?: T;
  error?: string;
  /** `data`가 지금 주소가 아니라 앞 주소의 답이다. 새 답을 기다리는 중이다 */
  stale: boolean;
  /** 같은 조건으로 다시 읽는다 */
  reload: () => void;
}

export function useQuery<T>(
  /** 조회 주소. `undefined`면 부르지 않는다 */
  url: string | undefined,
  schema: ResponseSchema<T>,
  /** 이 값이 바뀌면 조건이 같아도 다시 읽는다(SSE로 새 알림이 왔을 때 등) */
  version?: number,
): QueryState<T> {
  const [state, setState] = useState<{
    loading: boolean;
    data?: T;
    error?: string;
    /** `data`를 가져온 주소 */
    dataUrl?: string;
  }>({
    loading: url !== undefined,
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (url === undefined) {
      setState({ loading: false });
      return;
    }
    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true }));
    getJson(url, schema, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ loading: false, data, dataUrl: url });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({ loading: false, error: err instanceof Error ? err.message : String(err) });
      });
    return () => controller.abort();
    // 스키마는 모듈 상수라 의존성에 넣지 않는다. 넣으면 렌더마다 다시 부른다
  }, [url, version, tick]);

  const reload = useCallback(() => setTick((value) => value + 1), []);
  const stale = state.data !== undefined && state.dataUrl !== url;
  return { loading: state.loading, data: state.data, error: state.error, stale, reload };
}
